import { initI18n, ThemeProvider, Toaster } from '@btsis/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useRouteError,
} from 'react-router';
import { FieldLayout } from '@/layouts/field-layout';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { ErrorBoundary } from '@/lib/error-boundary';
import { OfflineProvider } from '@/lib/offline-context';
import { SignInPage } from '@/pages/sign-in-page';

initI18n();

const InboxPage = lazy(() => import('@/pages/inbox-page').then((m) => ({ default: m.InboxPage })));
const RecordPage = lazy(() =>
  import('@/pages/record-page').then((m) => ({ default: m.RecordPage })),
);
const ScanPage = lazy(() => import('@/pages/scan-page').then((m) => ({ default: m.ScanPage })));
const SyncPage = lazy(() => import('@/pages/sync-page').then((m) => ({ default: m.SyncPage })));

/**
 * A field phone is often on a connection that drops mid-request, so a read is
 * retried twice before it is called a failure, and a screen that comes back
 * into view asks again — which is the opposite of the admin's settings, and for
 * the opposite reason.
 */
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2, refetchOnWindowFocus: true, staleTime: 15_000 } },
});

function FullScreenLoader() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function RequireAuth() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/sign-in" replace />;
  return (
    <OfflineProvider>
      <Outlet />
    </OfflineProvider>
  );
}

function RedirectIfAuthenticated() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** A render error is rethrown past the router's own boundary to the app's. */
function RethrowRouteError(): never {
  throw useRouteError();
}

function RootRoute() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

/**
 * A data router, as the admin and the portal use, so a screen holding unsaved
 * work can block navigation with `useBlocker` when one needs to.
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootRoute />} errorElement={<RethrowRouteError />}>
      <Route element={<RedirectIfAuthenticated />}>
        <Route path="/sign-in" element={<SignInPage />} />
      </Route>
      <Route element={<RequireAuth />}>
        <Route element={<FieldLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <InboxPage />
              </Suspense>
            }
          />
          <Route
            path="records/:id"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <RecordPage />
              </Suspense>
            }
          />
          <Route
            path="scan"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <ScanPage />
              </Suspense>
            }
          />
          <Route
            path="sync"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <SyncPage />
              </Suspense>
            }
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>,
  ),
);

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <RouterProvider router={router} />
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
